import { useState, useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { motion } from 'framer-motion';
import { Phone, Mail, MessageCircle, Upload, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ADMIN_NOTIFICATION_EMAILS } from '@/lib/notifications';

const ContactSection = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [honeypot, setHoneypot] = useState(''); // bot trap — must stay empty
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check — silently drop if bot filled it
    if (honeypot.trim() !== '') {
      toast.success('Zpráva odeslána! / Message sent!');
      setForm({ name: '', email: '', phone: '', message: '' });
      return;
    }

    // Length validation
    if (form.name.length > 100 || form.email.length > 200 || form.phone.length > 50 || form.message.length > 5000) {
      toast.error('Zpráva je příliš dlouhá.');
      return;
    }
    if (form.message.trim().length < 5) {
      toast.error('Zpráva je příliš krátká.');
      return;
    }

    setSending(true);
    const id = crypto.randomUUID();
    const cleanName = form.name.trim().slice(0, 100);
    const cleanEmail = form.email.trim().slice(0, 200);
    const cleanPhone = (form.phone || '').trim().slice(0, 50);
    const cleanMessage = form.message.trim().slice(0, 5000);

    const { error } = await supabase.from('contact_messages').insert({
      id,
      name: cleanName,
      email: cleanEmail,
      phone: cleanPhone,
      message: cleanMessage,
    });
    if (error) {
      toast.error('Chyba při odesílání zprávy.');
      setSending(false);
      return;
    }

    // Fire-and-forget email notifications (don't block UX if email fails)
    // Send admin notification to all configured recipients (info@ + Gmail backup)
    ADMIN_NOTIFICATION_EMAILS.forEach((adminEmail) => {
      const slug = adminEmail.split('@')[0].replace(/[^a-z0-9]/gi, '');
      supabase.functions.invoke('send-transactional-email', {
        body: {
          templateName: 'admin-new-contact-message',
          recipientEmail: adminEmail,
          idempotencyKey: `admin-contact-${id}-${slug}`,
          templateData: { name: cleanName, email: cleanEmail, phone: cleanPhone, message: cleanMessage },
        },
      }).catch((err) => console.error(`Admin notification to ${adminEmail} failed:`, err));
    });

    supabase.functions.invoke('send-transactional-email', {
      body: {
        templateName: 'customer-contact-confirmation',
        recipientEmail: cleanEmail,
        idempotencyKey: `customer-contact-${id}`,
        templateData: { name: cleanName },
      },
    }).catch((err) => console.error('Customer confirmation failed:', err));

    toast.success('Zpráva odeslána! / Message sent!');
    setForm({ name: '', email: '', phone: '', message: '' });
    setFile(null);
    setSending(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type.startsWith('image/')) {
      setFile(droppedFile);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) setFile(selected);
  };

  const contactCards = [
    {
      href: 'https://wa.me/420739232870',
      target: '_blank',
      icon: MessageCircle,
      label: t.contact.whatsapp,
      detail: '+420 739 232 870',
    },
    {
      href: 'tel:+420739232870',
      icon: Phone,
      label: 'Telefon / Phone',
      detail: '+420 739 232 870',
    },
    {
      href: 'mailto:info@tomartmask.cz',
      icon: Mail,
      label: 'Email',
      detail: 'info@tomartmask.cz',
    },
  ];

  return (
    <section id="contact" className="py-20 md:py-32 px-4">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-7xl tracking-wider text-foreground" style={{ fontFamily: 'Bebas Neue' }}>
            {t.contact.title}
          </h2>
          <p className="mt-4 text-muted-foreground text-lg">{t.contact.subtitle}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Form */}
          <motion.form
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            {/* Honeypot — hidden from users, bots will fill it */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
              aria-hidden="true"
            />

            <Input
              placeholder={t.contact.name}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              maxLength={100}
              className="bg-card border-border text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/30 focus:border-primary/50 focus:shadow-[0_0_15px_hsl(var(--primary)/0.15)] transition-shadow"
            />
            <Input
              type="email"
              placeholder={t.contact.email}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              maxLength={200}
              className="bg-card border-border text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/30 focus:border-primary/50 focus:shadow-[0_0_15px_hsl(var(--primary)/0.15)] transition-shadow"
            />
            <Input
              placeholder={t.contact.phone}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              maxLength={50}
              className="bg-card border-border text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/30 focus:border-primary/50 focus:shadow-[0_0_15px_hsl(var(--primary)/0.15)] transition-shadow"
            />
            <Textarea
              placeholder={t.contact.message}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              required
              rows={5}
              maxLength={5000}
              className="bg-card border-border text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/30 focus:border-primary/50 focus:shadow-[0_0_15px_hsl(var(--primary)/0.15)] transition-shadow"
            />

            {/* Upload zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-sm p-6 text-center cursor-pointer transition-colors ${
                isDragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              {file ? (
                <div className="flex items-center justify-center gap-2 text-primary">
                  <Upload size={20} />
                  <span className="text-sm">{t.contact.fileSelected}: {file.name}</span>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                    <X size={16} className="text-muted-foreground hover:text-foreground" />
                  </button>
                </div>
              ) : (
                <div className="text-muted-foreground">
                  <Upload size={24} className="mx-auto mb-2" />
                  <p className="text-sm">{t.contact.uploadLabel}</p>
                  <p className="text-xs mt-1">{t.contact.uploadHint}</p>
                </div>
              )}
            </div>

            <Button type="submit" className="btn-shimmer w-full uppercase tracking-widest font-bold" disabled={sending}>
              {sending ? 'Odesílám...' : t.contact.send}
            </Button>
            <p className="text-xs text-muted-foreground/70 leading-relaxed pt-1">
              {t.contact.gdprNotice}{' '}
              <a href="/privacy" className="text-primary hover:underline">
                {t.contact.gdprLink}
              </a>
              .
            </p>
          </motion.form>

          {/* Contact info with staggered slide-in */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {contactCards.map((card, i) => (
              <motion.a
                key={i}
                href={card.href}
                target={card.target}
                rel={card.target ? 'noopener noreferrer' : undefined}
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                className="flex items-center gap-4 p-4 bg-card rounded-sm border border-border hover:border-primary/30 hover:shadow-[0_0_20px_hsl(var(--primary)/0.1)] transition-all duration-300"
              >
                <card.icon className="text-primary" size={24} />
                <div>
                  <p className="text-foreground font-semibold">{card.label}</p>
                  <p className="text-muted-foreground text-sm">{card.detail}</p>
                </div>
              </motion.a>
            ))}

            {/* Social links */}
            <div className="flex gap-4 pt-4">
              <a
                href="https://www.instagram.com/tom_art_mask"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-shimmer px-6 py-3 bg-card border border-border rounded-sm text-sm uppercase tracking-widest text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
              >
                Instagram
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=100087669492219"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-shimmer px-6 py-3 bg-card border border-border rounded-sm text-sm uppercase tracking-widest text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
              >
                Facebook
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
